/**
 * Email / mobile validation aligned with backend auth OTP rules (10–15 digits, digits only in request)
 * and staff email checks. Used by all frontend apps via @uniqcamp/shared.
 */

function toStr(v) {
  if (v == null) return '';
  return typeof v === 'string' ? v : String(v);
}

/** Strip to digits only (for SMS / OTP API bodies — matches auth route /^[0-9]{10,15}$/). */
export function mobileDigitsOnly(value) {
  return toStr(value).replace(/\D/g, '');
}

/** True when digit count is 10–15 after stripping non-digits. */
export function isValidMobileDigits(value) {
  const d = mobileDigitsOnly(value);
  return d.length >= 10 && d.length <= 15;
}

/** Non-empty trimmed string must look like an email (practical check, not full RFC). */
export function isValidEmail(value) {
  const s = toStr(value).trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(s);
}

/** Empty or whitespace-only is valid; otherwise same as {@link isValidEmail}. */
export function isValidEmailOptional(value) {
  const s = toStr(value).trim();
  if (!s) return true;
  return isValidEmail(s);
}

export function normalizeEmail(value) {
  return toStr(value).trim().toLowerCase();
}

/** Use when calling OTP APIs so +spaces pass backend express-validator. */
export function normalizeMobileForOtp(value) {
  return mobileDigitsOnly(value);
}

/**
 * @param {string} value
 * @param {{ required?: boolean, label?: string }} [options]
 * @returns {string|null} Error message or null if valid
 */
export function mobileValidationError(value, options) {
  const { required = false, label = 'Mobile' } = options || {};
  const s = toStr(value).trim();
  if (!s) {
    return required ? `${label} is required.` : null;
  }
  if (!isValidMobileDigits(s)) {
    return `${label} must be 10–15 digits (you can include spaces or +; they are ignored).`;
  }
  return null;
}

/**
 * @param {string} value
 * @param {{ required?: boolean, label?: string }} [options]
 * @returns {string|null} Error message or null if valid
 */
export function emailValidationError(value, options) {
  const { required = false, label = 'Email' } = options || {};
  const s = toStr(value).trim();
  if (!s) {
    return required ? `${label} is required.` : null;
  }
  if (!isValidEmail(s)) {
    return `Enter a valid ${label.toLowerCase()} address.`;
  }
  return null;
}
